<!--
  Version History Panel - View and Restore SAX Program Versions

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/VersionHistoryPanel.vue

  Displays version history with restore and compare capabilities
-->

<template>
  <q-dialog
    :model-value="modelValue"
    maximized
    transition-show="slide-left"
    transition-hide="slide-right"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card>
      <q-toolbar class="bg-primary text-white">
        <q-toolbar-title>
          <q-icon name="history" class="q-mr-sm" />
          Version History
        </q-toolbar-title>

        <q-btn flat round dense icon="close" @click="closeDialog" />
      </q-toolbar>

      <q-card-section class="q-pa-md">
        <!-- Toolbar -->
        <div class="row q-gutter-sm q-mb-md">
          <q-input
            v-model="searchQuery"
            dense
            outlined
            placeholder="Search versions..."
            class="col"
            clearable
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <q-select
            v-model="filterStatus"
            :options="statusOptions"
            dense
            outlined
            label="Status"
            clearable
            emit-value
            map-options
            style="min-width: 150px"
          />

          <q-btn-toggle
            v-model="viewMode"
            :options="viewModeOptions"
            dense
            outline
            color="primary"
          />

          <q-space />

          <q-btn
            icon="refresh"
            color="primary"
            outline
            @click="loadVersions"
          >
            <q-tooltip>Refresh</q-tooltip>
          </q-btn>
        </div>

        <!-- Stats -->
        <div class="row q-gutter-sm q-mb-md">
          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Total Versions</div>
              <div class="text-h6">{{ versions.length }}</div>
            </q-card-section>
          </q-card>

          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Published</div>
              <div class="text-h6 text-positive">{{ publishedVersions }}</div>
            </q-card-section>
          </q-card>

          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Draft</div>
              <div class="text-h6 text-warning">{{ draftVersions }}</div>
            </q-card-section>
          </q-card>

          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Current Version</div>
              <div class="text-h6 text-primary">{{ latestVersion?.version_number || '-' }}</div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Timeline View -->
        <div v-if="viewMode === 'timeline'" class="version-timeline">
          <q-timeline color="primary">
            <q-timeline-entry
              v-for="version in filteredVersions"
              :key="version.id"
              :title="`Version ${version.version_number}`"
              :subtitle="formatDate(version.created_at)"
              :icon="version.status === 'published' ? 'check_circle' : 'edit'"
              :color="version.status === 'published' ? 'positive' : 'warning'"
            >
              <div class="version-card">
                <q-card flat bordered>
                  <q-card-section>
                    <!-- Status Badge -->
                    <q-badge
                      :color="version.status === 'published' ? 'positive' : 'warning'"
                      class="q-mb-sm"
                    >
                      {{ version.status }}
                    </q-badge>

                    <!-- Change Summary -->
                    <div v-if="version.change_summary" class="text-body2 q-mb-sm">
                      {{ version.change_summary }}
                    </div>

                    <!-- Stats -->
                    <div class="text-caption text-grey-7 q-mb-sm">
                      <q-icon name="widgets" size="xs" />
                      {{ version.component_count }} components
                      <q-icon name="cable" size="xs" class="q-ml-sm" />
                      {{ version.link_count }} links
                    </div>

                    <!-- User -->
                    <div v-if="version.user_name" class="text-caption text-grey-6">
                      By {{ version.user_name }}
                    </div>
                  </q-card-section>

                  <q-card-actions>
                    <q-btn
                      label="Preview"
                      size="sm"
                      flat
                      color="primary"
                      @click="previewVersion(version)"
                    />

                    <q-btn
                      label="Restore"
                      size="sm"
                      flat
                      color="secondary"
                      @click="restoreVersion(version)"
                    />

                    <q-btn
                      v-if="versions.length > 1"
                      label="Compare"
                      size="sm"
                      flat
                      color="info"
                      @click="compareVersion(version)"
                    />

                    <q-space />

                    <q-btn
                      icon="delete"
                      size="sm"
                      flat
                      color="negative"
                      @click="deleteVersion(version)"
                    >
                      <q-tooltip>Delete version</q-tooltip>
                    </q-btn>
                  </q-card-actions>
                </q-card>
              </div>
            </q-timeline-entry>
          </q-timeline>

          <div v-if="filteredVersions.length === 0" class="text-center q-pa-xl text-grey-6">
            <q-icon name="inbox" size="5em" />
            <div class="text-h6 q-mt-md">No versions found</div>
          </div>
        </div>

        <!-- Table View -->
        <div v-if="viewMode === 'table'" class="version-table">
          <q-table
            :rows="filteredVersions"
            :columns="columns"
            row-key="id"
            :loading="isLoading"
            :pagination="pagination"
            dense
            flat
            bordered
          >
            <template #body-cell-version="props">
              <q-td :props="props">
                <div class="text-weight-medium">v{{ props.row.version_number }}</div>
              </q-td>
            </template>

            <template #body-cell-status="props">
              <q-td :props="props">
                <q-badge
                  :color="props.row.status === 'published' ? 'positive' : 'warning'"
                >
                  {{ props.row.status }}
                </q-badge>
              </q-td>
            </template>

            <template #body-cell-stats="props">
              <q-td :props="props">
                <div class="text-caption">
                  <q-icon name="widgets" size="xs" />
                  {{ props.row.component_count }}
                  <q-icon name="cable" size="xs" class="q-ml-xs" />
                  {{ props.row.link_count }}
                </div>
              </q-td>
            </template>

            <template #body-cell-actions="props">
              <q-td :props="props">
                <q-btn
                  icon="visibility"
                  size="sm"
                  flat
                  dense
                  color="primary"
                  @click="previewVersion(props.row)"
                >
                  <q-tooltip>Preview</q-tooltip>
                </q-btn>

                <q-btn
                  icon="restore"
                  size="sm"
                  flat
                  dense
                  color="secondary"
                  @click="restoreVersion(props.row)"
                >
                  <q-tooltip>Restore</q-tooltip>
                </q-btn>

                <q-btn
                  v-if="versions.length > 1"
                  icon="compare_arrows"
                  size="sm"
                  flat
                  dense
                  color="info"
                  @click="compareVersion(props.row)"
                >
                  <q-tooltip>Compare</q-tooltip>
                </q-btn>

                <q-btn
                  icon="delete"
                  size="sm"
                  flat
                  dense
                  color="negative"
                  @click="deleteVersion(props.row)"
                >
                  <q-tooltip>Delete</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>
        </div>
      </q-card-section>
    </q-card>

    <!-- Preview Dialog -->
    <q-dialog v-model="showPreviewDialog" maximized>
      <q-card>
        <q-toolbar class="bg-grey-2">
          <q-toolbar-title>
            Version {{ previewedVersion?.version_number }} Preview
          </q-toolbar-title>
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>

        <q-card-section v-if="previewedVersion">
          <div class="q-gutter-md">
            <!-- Version Info -->
            <q-card flat bordered>
              <q-card-section>
                <div class="row q-gutter-md">
                  <div>
                    <div class="text-caption text-grey-7">Version</div>
                    <div class="text-h6">{{ previewedVersion.version_number }}</div>
                  </div>
                  <div>
                    <div class="text-caption text-grey-7">Status</div>
                    <q-badge
                      :color="previewedVersion.status === 'published' ? 'positive' : 'warning'"
                    >
                      {{ previewedVersion.status }}
                    </q-badge>
                  </div>
                  <div>
                    <div class="text-caption text-grey-7">Created</div>
                    <div>{{ formatDate(previewedVersion.created_at) }}</div>
                  </div>
                  <div v-if="previewedVersion.user_name">
                    <div class="text-caption text-grey-7">By</div>
                    <div>{{ previewedVersion.user_name }}</div>
                  </div>
                </div>

                <div v-if="previewedVersion.change_summary" class="q-mt-md">
                  <div class="text-caption text-grey-7">Change Summary</div>
                  <div>{{ previewedVersion.change_summary }}</div>
                </div>
              </q-card-section>
            </q-card>

            <!-- Components -->
            <q-card flat bordered>
              <q-card-section>
                <div class="text-subtitle2 q-mb-sm">
                  Components ({{ previewedVersion.component_count }})
                </div>

                <q-list bordered separator>
                  <q-item
                    v-for="component in previewedVersion.programming_sax_data?.components"
                    :key="component.id"
                    dense
                  >
                    <q-item-section avatar>
                      <div
                        class="color-preview"
                        :style="{ backgroundColor: component.color || '#3B82F6' }"
                      ></div>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ component.name }}</q-item-label>
                      <q-item-label caption>{{ component.type }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-card-section>
            </q-card>

            <!-- Links -->
            <q-card flat bordered>
              <q-card-section>
                <div class="text-subtitle2 q-mb-sm">
                  Links ({{ previewedVersion.link_count }})
                </div>

                <q-list bordered separator>
                  <q-item
                    v-for="link in previewedVersion.programming_sax_data?.links"
                    :key="link.id"
                    dense
                  >
                    <q-item-section>
                      <q-item-label>
                        {{ getComponentName(link.from_component, previewedVersion) }}.{{ link.from_slot }}
                        →
                        {{ getComponentName(link.to_component, previewedVersion) }}.{{ link.to_slot }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-card-section>
            </q-card>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup label="Close" flat />
          <q-btn
            label="Restore This Version"
            color="primary"
            @click="restoreVersion(previewedVersion)"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Compare Dialog -->
    <q-dialog v-model="showCompareDialog" maximized>
      <q-card>
        <q-toolbar class="bg-grey-2">
          <q-toolbar-title>Compare Versions</q-toolbar-title>
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>

        <q-card-section>
          <div class="row q-gutter-sm q-mb-md">
            <q-select
              v-model="compareVersion1"
              :options="versionOptions"
              label="Version 1"
              dense
              outlined
              emit-value
              map-options
              option-value="id"
              :option-label="v => `v${v.version_number} - ${formatDate(v.created_at)}`"
              class="col"
            />

            <q-select
              v-model="compareVersion2"
              :options="versionOptions"
              label="Version 2"
              dense
              outlined
              emit-value
              map-options
              option-value="id"
              :option-label="v => `v${v.version_number} - ${formatDate(v.created_at)}`"
              class="col"
            />

            <q-btn
              label="Compare"
              color="primary"
              :disable="!compareVersion1 || !compareVersion2"
              @click="performCompare"
            />
          </div>

          <div v-if="compareResult" class="compare-result">
            <q-card flat bordered>
              <q-card-section>
                <div class="text-h6 q-mb-md">Differences</div>

                <div class="row q-gutter-sm q-mb-md">
                  <q-card flat bordered class="col">
                    <q-card-section class="q-pa-sm">
                      <div class="text-caption text-grey-7">Added Components</div>
                      <div class="text-h6 text-positive">{{ compareResult.added_components }}</div>
                    </q-card-section>
                  </q-card>

                  <q-card flat bordered class="col">
                    <q-card-section class="q-pa-sm">
                      <div class="text-caption text-grey-7">Removed Components</div>
                      <div class="text-h6 text-negative">{{ compareResult.removed_components }}</div>
                    </q-card-section>
                  </q-card>

                  <q-card flat bordered class="col">
                    <q-card-section class="q-pa-sm">
                      <div class="text-caption text-grey-7">Modified Components</div>
                      <div class="text-h6 text-warning">{{ compareResult.modified_components }}</div>
                    </q-card-section>
                  </q-card>
                </div>

                <div class="row q-gutter-sm">
                  <q-card flat bordered class="col">
                    <q-card-section class="q-pa-sm">
                      <div class="text-caption text-grey-7">Added Links</div>
                      <div class="text-h6 text-positive">{{ compareResult.added_links }}</div>
                    </q-card-section>
                  </q-card>

                  <q-card flat bordered class="col">
                    <q-card-section class="q-pa-sm">
                      <div class="text-caption text-grey-7">Removed Links</div>
                      <div class="text-h6 text-negative">{{ compareResult.removed_links }}</div>
                    </q-card-section>
                  </q-card>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup label="Close" flat />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import axios from 'axios';

  // Props
  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    equipmentId: {
      type: Number,
      required: true,
    },
    siteId: {
      type: Number,
      default: null,
    },
  });

  // Emits
  const emit = defineEmits(['update:modelValue', 'version-restored']);

  // Quasar
  const $q = useQuasar();

  // State
  const versions = ref([]);
  const isLoading = ref(false);
  const searchQuery = ref('');
  const filterStatus = ref(null);
  const viewMode = ref('timeline');
  const showPreviewDialog = ref(false);
  const showCompareDialog = ref(false);
  const previewedVersion = ref(null);
  const compareVersion1 = ref(null);
  const compareVersion2 = ref(null);
  const compareResult = ref(null);

  // Constants
  const statusOptions = [
    { label: 'All Status', value: null },
    { label: 'Published', value: 'published' },
    { label: 'Draft', value: 'draft' },
  ];

  const viewModeOptions = [
    { label: 'Timeline', value: 'timeline', icon: 'timeline' },
    { label: 'Table', value: 'table', icon: 'table_rows' },
  ];

  // Table columns
  const columns = [
    {
      name: 'version',
      label: 'Version',
      field: 'version_number',
      align: 'left',
      sortable: true,
    },
    {
      name: 'status',
      label: 'Status',
      field: 'status',
      align: 'center',
      sortable: true,
    },
    {
      name: 'change_summary',
      label: 'Change Summary',
      field: 'change_summary',
      align: 'left',
    },
    {
      name: 'stats',
      label: 'Stats',
      align: 'center',
    },
    {
      name: 'user',
      label: 'User',
      field: 'user_name',
      align: 'left',
    },
    {
      name: 'created',
      label: 'Created',
      field: 'created_at',
      align: 'left',
      sortable: true,
    },
    {
      name: 'actions',
      label: 'Actions',
      align: 'center',
    },
  ];

  const pagination = ref({
    rowsPerPage: 20,
    sortBy: 'version_number',
    descending: true,
  });

  // Computed

  const filteredVersions = computed(() => {
    let filtered = versions.value;

    // Search filter
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase();
      filtered = filtered.filter((v) =>
        v.change_summary?.toLowerCase().includes(query) ||
        v.user_name?.toLowerCase().includes(query),
      );
    }

    // Status filter
    if (filterStatus.value) {
      filtered = filtered.filter((v) => v.status === filterStatus.value);
    }

    return filtered;
  });

  const publishedVersions = computed(() =>
    versions.value.filter((v) => v.status === 'published').length,
  );

  const draftVersions = computed(() =>
    versions.value.filter((v) => v.status === 'draft').length,
  );

  const latestVersion = computed(() =>
    versions.value.length > 0 ? versions.value[0] : null,
  );

  const versionOptions = computed(() => versions.value);

  // Methods

  /**
   * Load versions
   */
  async function loadVersions() {
    isLoading.value = true;
    try {
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/documentation/sax/versions`
        : `/equipment/${props.equipmentId}/documentation/sax/versions`;

      const response = await axios.get(apiPath);
      versions.value = response.data.versions || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load versions',
        caption: error.message,
      });
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Preview version
   */
  function previewVersion(version) {
    previewedVersion.value = version;
    showPreviewDialog.value = true;
  }

  /**
   * Restore version
   */
  function restoreVersion(version) {
    $q.dialog({
      title: 'Restore Version',
      message: `Are you sure you want to restore to version ${version.version_number}? Current changes will be overwritten.`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const apiPath = props.siteId
          ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/documentation/sax/versions/${version.id}/restore`
          : `/equipment/${props.equipmentId}/documentation/sax/versions/${version.id}/restore`;

        await axios.post(apiPath);

        $q.notify({
          type: 'positive',
          message: `Restored to version ${version.version_number}`,
        });

        emit('version-restored', version);
        closeDialog();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to restore version',
          caption: error.message,
        });
      }
    });
  }

  /**
   * Compare version
   */
  function compareVersion(version) {
    compareVersion1.value = version.id;
    compareVersion2.value = latestVersion.value?.id;
    showCompareDialog.value = true;
  }

  /**
   * Perform compare
   */
  async function performCompare() {
    try {
      const v1 = versions.value.find((v) => v.id === compareVersion1.value);
      const v2 = versions.value.find((v) => v.id === compareVersion2.value);

      // Simple client-side comparison
      const data1 = v1.programming_sax_data || {};
      const data2 = v2.programming_sax_data || {};

      const comps1 = data1.components || [];
      const comps2 = data2.components || [];
      const links1 = data1.links || [];
      const links2 = data2.links || [];

      compareResult.value = {
        added_components: comps2.length - comps1.length,
        removed_components: Math.max(0, comps1.length - comps2.length),
        modified_components: 0, // Would need deeper comparison
        added_links: links2.length - links1.length,
        removed_links: Math.max(0, links1.length - links2.length),
      };
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to compare versions',
        caption: error.message,
      });
    }
  }

  /**
   * Delete version
   */
  function deleteVersion(version) {
    $q.dialog({
      title: 'Delete Version',
      message: `Are you sure you want to delete version ${version.version_number}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const apiPath = props.siteId
          ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/documentation/sax/versions/${version.id}`
          : `/equipment/${props.equipmentId}/documentation/sax/versions/${version.id}`;

        await axios.delete(apiPath);

        $q.notify({
          type: 'positive',
          message: 'Version deleted',
        });

        loadVersions();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to delete version',
          caption: error.message,
        });
      }
    });
  }

  /**
   * Get component name by ID
   */
  function getComponentName(componentId, version) {
    const component = version.programming_sax_data?.components?.find((c) => c.id === componentId);
    return component?.name || componentId;
  }

  /**
   * Format date
   */
  function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  /**
   * Close dialog
   */
  function closeDialog() {
    emit('update:modelValue', false);
  }

  // Watchers

  watch(
    () => props.modelValue,
    (newValue) => {
      if (newValue) {
        loadVersions();
      }
    },
  );
</script>

<style scoped lang="scss">
.version-timeline {
  .version-card {
    max-width: 600px;
  }
}

.color-preview {
  width: 30px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
</style>
